import React from 'react';
import { SESSION_ERROR_TITLE } from '@curvebeamai/web-connect-service';
import ErrorPage from '../components/ErrorPage';
import SessionStatusPage from '../components/SessionStatusPage';
import { SessionConnectionStateEnum } from '../models/SessionConnectionStateEnum';
import type { SessionFailureKind } from '../services/session-channel.service';

/**
 * [CurveBeamAI] Resolves app initialization state to appropriate status/error message.
 * Mirrors develop branch behavior: shows pending DMP access token validation and app
 * initialization messages when the viewer loads for the first time.
 *
 * Priority order:
 * 1. initError → show error message
 * 2. useSessionChannelFlow + PENDING → "Pending DMP Access Token validation..."
 * 3. init === null → "App initializing..."
 *
 * [WTD-1826] ErrorPage is a bare message by design, because cases 2 and 3 are progress reports and
 * a 401 heading over "App initializing..." would be nonsense. A configuration fault is not progress
 * though, and rendering it the same way left it as one unexplained line. It gets SessionStatusPage
 * instead - the component /invalid-source and /session-expired already use - so the three states a
 * session can fail in look like each other.
 *
 * Only for 'config'. An appInit crash keeps the plain treatment: its message names an extension
 * that would not load, and titling that "401 - Session error" would point at a session that is
 * perfectly healthy.
 */
export function appInitErrorResolver(
  init: unknown,
  sessionState: SessionConnectionStateEnum,
  initError: string,
  useSessionChannelFlow: boolean,
  sessionFailure: SessionFailureKind | null = null
): React.ReactElement | null {
  if (initError !== '') {
    return sessionFailure === 'config' ? (
      <SessionStatusPage
        title={SESSION_ERROR_TITLE}
        message={initError}
      />
    ) : (
      <ErrorPage message={initError} />
    );
  }
  if (useSessionChannelFlow && sessionState === SessionConnectionStateEnum.PENDING) {
    return <ErrorPage message="Pending DMP Access Token validation..." />;
  }
  if (init === null) {
    return <ErrorPage message="App initializing..." />;
  }
  return null;
}
